import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "lucide-react";
import { computePickPerformance, formatPct, EXPECTANCY_EXPLAINER } from "@/lib/pickStats";

interface PickRow {
  status: string;
  published_at: string | null;
  recorded_at: string;
  resolved_at: string | null;
  current_pct: number | null;
}


/**
 * Proof-point strip: real counts pulled from the append-only pick ledger
 * (same source as /track-record). No marketing copy.
 */
export function TrackRecordTicker() {
  const { data, isLoading } = useQuery({
    queryKey: ["home-track-record-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pick_history" as any)
        .select("status, published_at, recorded_at, resolved_at, current_pct")
        .order("recorded_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as unknown as PickRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const picks = data ?? [];
  const weekAgo = Date.now() - 7 * 86_400_000;
  const publishedThisWeek = picks.filter(
    (p) => new Date(p.published_at ?? p.recorded_at).getTime() >= weekAgo,
  ).length;
  const resolvedRows = picks.filter((p) => p.status !== "pending");
  const perf = computePickPerformance(picks);

  const holds = resolvedRows
    .map((p) => {
      const start = new Date(p.published_at ?? p.recorded_at).getTime();
      const end = p.resolved_at ? new Date(p.resolved_at).getTime() : NaN;
      return Number.isFinite(end) && end > start ? (end - start) / 86_400_000 : null;
    })
    .filter((d): d is number => d !== null);
  const avgHold = holds.length ? holds.reduce((a, b) => a + b, 0) / holds.length : null;

  const cells: { label: string; value: string; note?: string; title?: string }[] = [
    { label: "Picks published (7d)", value: String(publishedThisWeek) },
    { label: "Picks resolved", value: String(resolvedRows.length), note: `${picks.length - resolvedRows.length} open` },
    {
      label: "Win rate",
      value: perf.winRate === null ? "—" : `${perf.winRate.toFixed(0)}%`,
      note: resolvedRows.length ? `${perf.wins}/${resolvedRows.length} resolved` : "no resolved picks yet",
    },
    { label: "Avg win", value: formatPct(perf.avgWin), note: "resolved picks in profit" },
    { label: "Avg loss", value: formatPct(perf.avgLoss), note: "resolved picks in the red" },
    {
      label: "Expectancy",
      value: formatPct(perf.expectancy),
      note: "avg result per pick",
      title: EXPECTANCY_EXPLAINER,
    },
    { label: "Avg hold time", value: avgHold === null ? "—" : `${avgHold.toFixed(1)}d`, note: "to resolution" },
  ];


  return (
    <section aria-label="Track record" className="border-b border-border/60 bg-background">
      <div className="container mx-auto px-4 py-3">
        {isLoading ? (
          <Skeleton className="h-16" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-4 gap-y-3">
            {cells.map((c) => (
              <div key={c.label} className="min-w-0" title={c.title}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold truncate">
                  {c.label}
                </div>
                <div className="font-mono text-xl lg:text-2xl font-bold tabular-nums leading-tight">{c.value}</div>
                {c.note && <div className="text-[11px] text-muted-foreground truncate">{c.note}</div>}
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span>
            Rule: +15% = win, −10% = loss, 7-day window. Resolved daily, append-only. {EXPECTANCY_EXPLAINER}
          </span>

          <Link to="/track-record" className="inline-flex items-center gap-1 text-primary hover:underline">
            Full ledger <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </section>
  );
}
